import React from 'react';

const FrameworkSection = ({ title, items }) => (
  <div className="bg-white rounded-lg shadow p-4 mb-4">
    <h3 className="font-bold text-lg mb-3 text-blue-600">{title}</h3>
    <ul className="space-y-4">
      {items.map((item, index) => (
        <li key={index} className="border-b pb-2">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium">{item.name}</span>
            <span className="text-gray-600 text-sm">{item.version}</span>
          </div>
          <p className="text-sm text-gray-600 mb-2">{item.description}</p>
          <div className="bg-gray-50 p-2 rounded text-sm">
            <p className="font-medium text-gray-700 mb-1">Utilisation :</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              {item.usages.map((usage, i) => (
                <li key={i}>{usage}</li>
              ))}
            </ul>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

export default function ProjectSetup() {
  const frameworks = {
    core: [
      { 
        name: 'Java', 
        version: '17',
        description: 'Langage de base avec support LTS (Long Term Support)',
        usages: [
          'Langage principal de développement',
          'Support des fonctionnalités modernes (records, pattern matching)',
          'Performance et stabilité optimales'
        ]
      },
      { 
        name: 'Spring Boot', 
        version: '3.2.2',
        description: 'Framework principal pour le développement d\'applications d\'entreprise',
        usages: [
          'Auto-configuration des composants',
          'Intégration des différents modules Spring',
          'Gestion du cycle de vie de l\'application',
          'Configuration externalisée'
        ]
      },
      {
        name: 'Spring Cache',
        version: '3.2.2',
        description: 'Système de cache abstrait pour améliorer les performances',
        usages: [
          'Mise en cache des résultats de méthodes',
          'Réduction des appels aux services externes',
          'Cache conditionnel avec gestion TTL',
          'Support de multiples providers de cache'
        ]
      },
      {
        name: 'Spring Redis',
        version: '3.2.2',
        description: 'Intégration Redis pour le cache distribué',
        usages: [
          'Cache distribué haute performance',
          'Stockage de sessions utilisateurs',
          'Gestion des verrous distribués',
          'Pub/Sub pour la communication entre services'
        ]
      }
    ],
    webServices: [
      {
        name: 'Spring WS Core',
        version: '3.2.2',
        description: 'Framework pour les services web SOAP',
        usages: [
          'Création de services SOAP',
          'Génération automatique des WSDL',
          'Mapping XML/Java automatique',
          'Support des standards WS-*'
        ]
      },
      {
        name: 'Spring WS Security',
        version: '3.2.2',
        description: 'Module de sécurité pour les services web',
        usages: [
          'Sécurisation des messages SOAP',
          'Support des signatures XML',
          'Chiffrement des messages',
          'Authentification WS-Security'
        ]
      }
    ],
    persistence: [
      { 
        name: 'Spring Data JPA', 
        version: '3.2.2',
        description: 'Framework de persistance des données',
        usages: [
          'Mapping Objet-Relationnel',
          'Repositories avec méthodes générées',
          'Gestion des transactions',
          'Requêtes personnalisées avec QueryDSL'
        ]
      },
      { 
        name: 'PostgreSQL', 
        version: 'Runtime',
        description: 'Base de données SQL robuste et performante',
        usages: [
          'Stockage persistant des données',
          'Support des transactions ACID',
          'Types de données avancés (JSON, Arrays)',
          'Fonctions et triggers'
        ]
      }
    ],
    security: [
      { 
        name: 'Spring Security', 
        version: '3.2.2',
        description: 'Framework complet de sécurité',
        usages: [
          'Authentification et autorisation',
          'Protection CSRF',
          'Gestion des sessions',
          'Configuration sécurité fine'
        ]
      },
      { 
        name: 'JJWT', 
        version: '0.11.5',
        description: 'Bibliothèque pour JSON Web Tokens',
        usages: [
          'Création de tokens JWT',
          'Validation et parsing de tokens',
          'Support des algorithmes de signature',
          'Gestion des claims personnalisés'
        ]
      }
    ],
    stateManagement: [
      {
        name: 'Spring State Machine',
        version: '3.2.1',
        description: 'Gestion des machines à états',
        usages: [
          'Workflows de paiement',
          'Transitions d\'états avec garde',
          'Actions sur transitions',
          'Persistance des états'
        ]
      }
    ],
    utils: [
      {
        name: 'Lombok',
        version: 'Latest',
        description: 'Réduction du code boilerplate',
        usages: [
          'Génération de getters/setters',
          'Constructeurs automatiques',
          'Logging simplifié',
          'Classes immutables'
        ]
      },
      {
        name: 'Apache Commons Lang3',
        version: 'Latest',
        description: 'Bibliothèque d\'utilitaires Java',
        usages: [
          'Manipulation de strings',
          'Utilitaires d\'objets',
          'Gestion des exceptions',
          'Validations communes'
        ]
      },
      {
        name: 'Http Client',
        version: '4.5.14',
        description: 'Client HTTP Apache',
        usages: [
          'Appels REST',
          'Configuration SSL',
          'Gestion du pooling',
          'Intercepteurs de requêtes'
        ]
      }
    ],
    xml: [
      {
        name: 'JAXB',
        version: '4.0.2',
        description: 'API de binding XML-Java',
        usages: [
          'Mapping XML vers objets Java',
          'Génération de classes depuis XSD',
          'Sérialisation/désérialisation XML',
          'Validation de schéma'
        ]
      }
    ],
    testing: [
      { 
        name: 'Spring Boot Test', 
        version: '3.2.2',
        description: 'Framework de test complet',
        usages: [
          'Tests d\'intégration Spring',
          'MockMvc pour tests REST',
          'Configuration de test',
          'TestContainers pour BDD'
        ]
      },
      { 
        name: 'Spring Security Test', 
        version: '3.2.2',
        description: 'Support des tests de sécurité',
        usages: [
          'Simulation d\'authentification',
          'Test des autorisations',
          'Mock des contextes de sécurité',
          'Assertions de sécurité'
        ]
      }
    ]
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Frameworks & Dépendances</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(frameworks).map(([key, items]) => (
            <FrameworkSection key={key} title={key.charAt(0).toUpperCase() + key.slice(1)} items={items} />
          ))}
        </div>
      </div>
    </div>
  );
}